import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Card,
  CardContent,
} from '@mui/material';
import {jwtDecode} from 'jwt-decode';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { usePermissions } from '../../context/PermissionContext';
import Loader from '../General/Loader'; // Adjust the import path as needed
import BackButton from '../../components/BackButton';

const SetGST = () => {
  const [currentGST, setCurrentGST] = useState(null);
  const [newGST, setNewGST] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('success');
  const { can } = usePermissions();
  const canEdit = can('master.gst.edit');

  useEffect(() => {
    const token = sessionStorage.getItem('token');
    if (token) {
      try {
        jwtDecode(token);
        fetchCurrentGST();
      } catch (error) {
        console.error('Error decoding token:', error);
        setSnackSeverity('error');
        setSnackMessage('Authentication error. Please log in again.');
        setSnackOpen(true);
        setLoading(false);
      }
    } else {
      setSnackSeverity('error');
      setSnackMessage('Authentication required. Please log in.');
      setSnackOpen(true);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCurrentGST = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const decodedToken = jwtDecode(token);
      const userId = decodedToken.userId;

      const response = await axios.get(`${API_BASE_URL}/divisions/gst/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setCurrentGST(response.data.gst);
    } catch (error) {
      console.error('Error fetching GST:', error);
      setSnackSeverity('error');
      setSnackMessage('Failed to fetch current GST.');
      setSnackOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleGSTChange = (e) => {
    setNewGST(e.target.value);
  };

  const handleUpdateClick = () => {
    if (!canEdit) return;
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleConfirmUpdate = async () => {
    if (!canEdit) return;
    setDialogOpen(false);
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const decodedToken = jwtDecode(token);
      const userId = decodedToken.userId;

      const response = await axios.post(
        `${API_BASE_URL}/divisions/gst/${userId}`,
        { gst: parseFloat(newGST) },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setSnackSeverity('success');
      setSnackMessage(response.data.message || 'GST updated successfully.');
      setSnackOpen(true);
      setNewGST('');
      fetchCurrentGST();
    } catch (error) {
      console.error('Error updating GST:', error);
      setSnackSeverity('error');
      setSnackMessage(error.response?.data?.message || 'Failed to update GST.');
      setSnackOpen(true);
      setLoading(false);
    }
  };

  const handleSnackClose = () => {
    setSnackOpen(false);
  };

  if (loading) {
    return <Loader />;
  }

  return (
     <Box sx={{ position: 'relative', padding: 2 }}>
                  {/* Place BackButton outside the Grid */}
                  <BackButton />
                  <br/>  <br/>
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 5 }}>
      <Card sx={{ p: 3, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ fontFamily: 'Merriweather, serif' }}>
            Set GST
          </Typography>
          {currentGST !== null ? (
            <>
              <Typography variant="h6" gutterBottom>
                Current GST: {currentGST}%
              </Typography>
              {canEdit && (
                <>
                  <TextField
                    label="New GST"
                    type="number"
                    fullWidth
                    margin="normal"
                    variant="outlined"
                    value={newGST}
                    onChange={handleGSTChange}
                    InputProps={{ inputProps: { min: 0, max: 100 } }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateClick}
                    disabled={newGST === '' || newGST < 0 || newGST > 100}
                    sx={{ mt: 2 }}
                  >
                    Update GST
                  </Button>
                </>
              )}
            </>
          ) : (
            <Typography variant="body1" color="textSecondary">
              No GST value set.
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Confirm Update</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to update the GST to {newGST}%?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmUpdate} color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleSnackClose} severity={snackSeverity} sx={{ width: '100%' }}>
          {snackMessage}
        </Alert>
      </Snackbar>
    </Box>
    </Box>
  );
};

export default SetGST;
